import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react"

import { N0DES_API_PREFIX } from "@/api/domain"
import { Tag } from "@/api/tags"
import type { BackendBillingPlanName, BillingPeriod } from "@/billing/billing"
import type { GitHubRepoVisibility } from "@/github"
import type {
  Project,
  ProjectIdParam,
  ProjectMember,
  ProjectRole,
  User,
  UserProject,
  Uuid,
} from "@/types"

type ProjectUpdates = {
  name?: string
}

export type CreateProjectRequest = {
  name: string
  plan: BackendBillingPlanName
  billing_period: BillingPeriod
  billing_name: string
  billing_email: string
  promo_code_id?: string
  payment_method?: string
  members: [Uuid, ProjectRole][]
  // GitHub integration fields for free plans
  github_repo_url?: string
  repo_visibility?: GitHubRepoVisibility
  // TODO - not yet supported on the backend
  // template_source?: string
}

export type ProjectRoleRes = {
  role: ProjectRole
}

// Define a service using a base URL and expected endpoints
export const projectsApi = createApi({
  reducerPath: "projectsApi",
  tagTypes: [Tag.MyProjects],
  baseQuery: fetchBaseQuery({
    baseUrl: `${N0DES_API_PREFIX}projects`,
    mode: "cors",
    credentials: "include",
  }),
  endpoints: (build) => ({
    projectsPost: build.mutation<User, CreateProjectRequest>({
      query: (body) => ({
        url: "",
        method: "POST",
        body,
      }),
      invalidatesTags: [Tag.MyProjects],
    }),
    projectsGet: build.query<Project, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
      }),
    }),
    projectsGetByName: build.query<
      Project,
      { username: string; project_name: string }
    >({
      query: (params) => ({
        url: "",
        params,
      }),
    }),
    projectsPatch: build.mutation<
      Project,
      ProjectIdParam & { updates: ProjectUpdates }
    >({
      query: ({ project_id, updates }) => ({
        url: project_id,
        method: "PATCH",
        body: updates,
      }),
    }),
    projectsDelete: build.mutation<void, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: project_id,
        method: "DELETE",
      }),
      invalidatesTags: [Tag.MyProjects],
    }),
    projectsGetMyProjects: build.query<UserProject[], void>({
      query: () => ({
        url: "my-projects",
      }),
      providesTags: [Tag.MyProjects],
    }),
    projectsGetMyRole: build.query<ProjectRoleRes, ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/my-role`,
      }),
    }),
    projectsGetMembers: build.query<ProjectMember[], ProjectIdParam>({
      query: ({ project_id }) => ({
        url: `${project_id}/members`,
        method: "GET",
      }),
    }),
    projectsDeleteMembers: build.mutation<
      void,
      ProjectIdParam & { user_id: Uuid }
    >({
      query: ({ project_id, user_id }) => ({
        url: `${project_id}/members`,
        method: "DELETE",
        mode: "cors",
        credentials: "include",
        body: [user_id],
      }),
    }),
  }),
})

export const {
  useProjectsGetQuery,
  useProjectsGetByNameQuery,
  useProjectsPostMutation,
  useProjectsPatchMutation,
  useProjectsDeleteMutation,
  useProjectsGetMembersQuery,
  useProjectsDeleteMembersMutation,

  useProjectsGetMyProjectsQuery,
  useProjectsGetMyRoleQuery,
} = projectsApi
